import { format, formatDistanceToNow, isValid, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';

/**
 * Helpers de date au format Harmaury — locale fr, fuseau cible Africa/Douala (UTC+1, pas de DST).
 *
 * Note : date-fns n'a pas de support timezone natif. On utilise Intl.DateTimeFormat
 * pour les formats absolus quand un fuseau est nécessaire, et date-fns pour le relatif.
 *
 * Tous les formateurs sont défensifs : `Intl.DateTimeFormat.format` / `date-fns`
 * lèvent « Invalid time value » sur une date invalide — on renvoie `''` plutôt
 * que de planter l'écran appelant.
 */

const TIMEZONE = 'Africa/Douala';

/** `Date` valide, ou `null` si l'entrée est absente / non parsable. */
function toDate(input: Date | string | number | null | undefined): Date | null {
  if (input === null || input === undefined || input === '') return null;
  const date =
    input instanceof Date ? input : typeof input === 'string' ? parseISO(input) : new Date(input);
  return isValid(date) ? date : null;
}

/** "3 juin 2026" (fuseau Africa/Douala). */
export function formatDate(input: Date | string | number): string {
  const date = toDate(input);
  if (!date) return '';
  return new Intl.DateTimeFormat('fr-FR', {
    timeZone: TIMEZONE,
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(date);
}

/** "3 juin 2026, 14:32" (fuseau Africa/Douala). */
export function formatDateTime(input: Date | string | number): string {
  const date = toDate(input);
  if (!date) return '';
  return new Intl.DateTimeFormat('fr-FR', {
    timeZone: TIMEZONE,
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

/** "14:32" (heure locale Africa/Douala). */
export function formatTime(input: Date | string | number): string {
  const date = toDate(input);
  if (!date) return '';
  return new Intl.DateTimeFormat('fr-FR', {
    timeZone: TIMEZONE,
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

/** Formate une durée en minutes en libellé court ("1 h 30", "45 min"). */
export function formatDureeMinutes(minutes: number | null | undefined): string | null {
  if (minutes === null || minutes === undefined || minutes < 0) return null;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h > 0 && m > 0) return `${h} h ${m} min`;
  if (h > 0) return `${h} h`;
  return `${m} min`;
}

/** "il y a 5 minutes" — utile pour l'activité en direct. */
export function formatRelative(input: Date | string | number): string {
  const date = toDate(input);
  if (!date) return '';
  return formatDistanceToNow(date, { locale: fr, addSuffix: true });
}

/** Format technique ISO "yyyy-MM-dd" pour les API. */
export function formatISODate(input: Date | string | number): string {
  const date = toDate(input);
  if (!date) return '';
  return format(date, 'yyyy-MM-dd');
}
